// graft(#97c): copied from genome/v4 sdk/src/router/route-params.tsx — v5 owns this module, no live dep on v3/v4.
/**
 * Path-param matching + a route context, layered over the one-genome route
 * table. The SDK owns the resolution contract (which route + which params a
 * pathname yields); the APP owns history/navigation and feeds `navigate` into
 * the context. Plugins read `useRouteParams()` to get their path params (e.g.
 * `/studio-3d/:projectName` → `{ projectName }`) without importing the app.
 *
 * @module @ubiclouds/v5-sdk/router
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { ResolvedRoute } from './route-resolution.js';

export interface RouteMatch {
  readonly route: ResolvedRoute;
  /** Captured path params, e.g. `{ projectName: 'my-bot' }` (decoded). */
  readonly params: Record<string, string>;
}

/**
 * Match a pathname against the route table. Exact matches win over parameterised
 * ones; a `:name` segment captures one decoded path segment. Trailing slashes are
 * ignored. Returns null when nothing matches (the app renders its not-found).
 */
export function matchRoute(routes: readonly ResolvedRoute[], pathname: string): RouteMatch | null {
  const reqSegs = splitPath(pathname);
  // Exact (no params) first — a literal route always beats a param route.
  for (const route of routes) {
    if (!route.path.includes(':') && segEq(splitPath(route.path), reqSegs)) {
      return { route, params: {} };
    }
  }
  for (const route of routes) {
    if (!route.path.includes(':')) continue;
    const params = matchParams(splitPath(route.path), reqSegs);
    if (params) return { route, params };
  }
  return null;
}

function splitPath(path: string): string[] {
  return path.split('/').filter(Boolean);
}

function segEq(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

/** Match a parameterised route's segments against request segments → params, or null. */
function matchParams(routeSegs: readonly string[], reqSegs: readonly string[]): Record<string, string> | null {
  if (routeSegs.length !== reqSegs.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < routeSegs.length; i += 1) {
    const r = routeSegs[i]!;
    const v = reqSegs[i]!;
    if (r.startsWith(':')) {
      try {
        params[r.slice(1)] = decodeURIComponent(v);
      } catch {
        params[r.slice(1)] = v;
      }
    } else if (r !== v) {
      return null;
    }
  }
  return params;
}

export interface RouteContextValue {
  /** Captured path params for the active route. */
  readonly params: Record<string, string>;
  /** Navigate to a path (pushState + re-render); the app supplies the impl. */
  readonly navigate: (path: string) => void;
}

const RouteContext = createContext<RouteContextValue>({ params: {}, navigate: () => {} });

export function RouteParamsProvider({
  value,
  children,
}: {
  value: RouteContextValue;
  children: ReactNode;
}): ReactNode {
  return <RouteContext.Provider value={value}>{children}</RouteContext.Provider>;
}

/** Read the active route's params + the app's navigate fn. */
export function useRouteParams(): RouteContextValue {
  return useContext(RouteContext);
}
